import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DialerService } from 'dialer';
import { BsModalService } from 'ngx-bootstrap/modal';
import { RxDocumentBase } from "rxdb";
import { asyncScheduler, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AppState } from '../../core';
import {
  CorrelationRecord,
  DriveTest,
  isAndroid,
  isIos,
  MapImageOverlay, MapRouteGPXOverlay,
  MiscLeafNode,
  PathTest,
  PathTestMapPoint,
  PathTestPoint,
  PathTestPointSeq
} from '../../core/models';
import { DriveTestSeqDoc } from '../../core/schema/drive-test-seq-schema';
import { TestSeqDoc } from '../../core/schema/test-seq-schema';
import { DialerClientService, DriveTestSeqService, PathWaypointService, TimeStatusResp } from '../../core/services';
import { TestSeqService } from '../../core/services/test-seq.service';
import { DialerClientMonitorComponent } from '../../dialer-client/dialer-client-monitor/dialer-client-monitor.component';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { PathViewStore } from '../path-test.store';
import { PathTestPointLogComponent, PathTestPointSeqSavedEvent } from './path-test-point-log/path-test-point-log.component';
import { PathTestPointSeqDataEditorComponent } from './path-test-point-seq-data-editor/path-test-point-seq-data-editor.component';
import { PathTestPointSeqStatusEditorComponent } from './path-test-point-seq-status-editor/path-test-point-seq-status-editor.component';
import { CorrelationTableComponent, correlationDefaultStatGroups } from '../../post-processing/correlation-table/correlation-table.component';
import { PathTestPlotterComponent } from '../path-test-plotter/path-test-plotter.component';
import { PathKmlViewerModal } from '../../path/path-kml-viewer/path-kml-viewer-modal/path-kml-viewer-modal.component';
import { TableViewContext } from '../../shared/components/table-view/table-view-component-base';


@Component({
  selector: 'app-path-test-point',
  templateUrl: './path-test-point.component.html',
  styleUrls: ['./path-test-point.component.scss']
})
export class PathTestPointComponent implements OnInit, OnDestroy {

  @Input() pathTestOverlay: MapImageOverlay
  @Input() routeGPX: MapRouteGPXOverlay

  @Input() changeSelectedMapPointTestGuard: ( current: PathTestMapPoint, acceptFn: () => void ) => void

  @Input() bTabActive: boolean = false

  @Output('updateContent') updateContent$ = new EventEmitter<void>()

  @ViewChild('seqEditor') seqEditor :PathTestPointSeqDataEditorComponent
  @ViewChild('statusEditor') seqStatusEditor :PathTestPointSeqStatusEditorComponent
  @ViewChild('seqLogComponent') seqLogComponent :PathTestPointLogComponent
  @ViewChild('dialerClientMonitor') set dialerClientMonitorSetter (c: DialerClientMonitorComponent) {
    asyncScheduler.schedule( () => this.dialerClientMonitor = c)
  }

  pathTestDoc: (RxDocumentBase<PathTest, {}> & PathTest) | (RxDocumentBase<DriveTest, {}> & DriveTest);
  dialerClientMonitor: DialerClientMonitorComponent
  bShowMapInEditor = false
  pathPlotterCssClass = 'small-plotter'
  bResizeMap = false
  correlationStatGroups = correlationDefaultStatGroups.filter(g => ['byCarrier', 'byOs', 'byModel'].includes(g.id));

  constructor(
    public appState: AppState,
    public pathViewStore: PathViewStore,
    public testSeqService: TestSeqService,
    public dialerService: DialerService,
    public driveTestSeqService: DriveTestSeqService,
    public dialerClientService: DialerClientService,
    public bsModalService: BsModalService,
    protected pathWaypointService: PathWaypointService,
  ) {
    this.pathPlotterCssClass = this.appState.settings.getPathTestPointEditorPlotterCssClass()
  }

  testType: 'path test' | 'drive test' | 'scout';
  testSeqDoc : TestSeqDoc | DriveTestSeqDoc
  testSeqDocSub : Subscription

  ngOnInit(): void {
    this.pathViewStore.pathTestDoc$.subscribe((pathTest)=>{
      this.testType = pathTest?.type
      this.pathTestDoc=pathTest
      if (this.testType=='drive test') {
        this.testSeqDocSub = this.pathViewStore.seq$.pipe(
            mergeMap(seq => seq?.dialerSeqId ? this.driveTestSeqService.rxCollection.findOne(seq.dialerSeqId).$ : of(null))
        ).subscribe(testSeqDoc => {
          this.testSeqDoc = testSeqDoc
        })
      } else {
        this.testSeqDocSub = this.pathViewStore.seq$.pipe(
            mergeMap(seq=> seq?.dialerSeqId ? this.testSeqService.rxCollection.findOne(seq.dialerSeqId).$ : of(null))
        ).subscribe( testSeqDoc => {
          this.testSeqDoc = testSeqDoc
        })
      }
    })
  }

  ngOnDestroy(): void {
    this.testSeqDocSub?.unsubscribe()
  }

  showMapInEditor() {
    this.bShowMapInEditor = true
    this.onResizeMap(this.pathPlotterCssClass)
  }

  onResizeMap( cssClass : string ) {
    console.log(`onResizeMap(${cssClass})`)
    if ( !cssClass ) {
      cssClass = 'small-plotter'
    }
    if ( cssClass != this.appState.settings.getPathTestPointEditorPlotterCssClass() ) {
      try {
        this.appState.settings.setPathTestPointEditorPlotterCssClass(cssClass)
      } catch ( err ) {
        console.error(err)
      }
    }
    this.pathPlotterCssClass=cssClass
    this.bResizeMap = true
    asyncScheduler.schedule( _ => this.bResizeMap=false )
  }

  onTestRestart( testPoint : PathTestPoint ) {
    //this.testPoint = testPoint
    //this.pathTestPointComponent.loadTestPointData()
  }

  onNewSeqClick() {
    this.changeSelectedPointTestGuard( ()=> {
      this.pathViewStore.newSeq()
      asyncScheduler.schedule( () => this.seqEditor.isEditing = true )
    })
  }

  async onSeqEditCancelled() {
    console.log(`onSeqEditCancelled()`)
    const state = await firstValueFrom(this.pathViewStore.state$);
    if ( !state.pathTestDoc?.testPoints?.find(s=>s.id==state.seq.id) ) {
      this.pathViewStore.changeTestPoint( state.testPoint )
    }
  }

  async onSeqEditSaved() {
    console.log(`onSeqEditSaved()`)
    const state = await firstValueFrom(this.pathViewStore.state$);
    //TBD - update the test point status based on start/stop times of latest seq?
  }


  isLocalTestInProgress = () => this.seqStatusEditor?.isLocalTestInProgress()

  isEditing = () : boolean => this.seqEditor?.isModified() || this.seqLogComponent?.isEditing;

  changeSelectedPointTestGuard = ( acceptFn: () => void ) => {
    if ( !this.isEditing() ) {
      acceptFn()
    } else {
      const resetFormAndChangeWaypoint = () => {
        this.seqEditor.onEditClick()
        this.seqEditor.taskForm.reset()
        this.seqLogComponent?.comments.reset()
        acceptFn()
      }
      this.seqEditor.askDiscardEdits(resetFormAndChangeWaypoint)
    }
  }

  askDiscardEdits = async ( discardActionFn: () => void ) => {
    let approvedDiscard : boolean = true;
    if ( this.isEditing() ) {
      approvedDiscard = false;
      await this.seqEditor.askDiscardEdits( () => approvedDiscard = true );
    }
    if ( approvedDiscard && this.isLocalTestInProgress() ) {
      approvedDiscard = false;
      let initialState = {
        onYes: () => {
          approvedDiscard = true;
        },
        question: 'A test is in progress.  Are you sure you want to leave?',
        focusOnNo: true,
        yesStyle: 'btn btn-danger'
      }
      const modalRef = this.bsModalService.show(ConfirmModalComponent, { initialState });
      await firstValueFrom(modalRef.onHidden);
      if ( approvedDiscard ) {
        await this.seqStatusEditor?.saveTestSeqRootTest();
      }
    }
    if ( approvedDiscard ) {
      discardActionFn();
    }
  }

  async onPlotterPointSelected(mapPoint : PathTestMapPoint) {
    const state = await firstValueFrom(this.pathViewStore.state$)
    if ( mapPoint.pathTestPoint.id != state.testPoint?.id ) {
      this.changeSelectedPointTestGuard(
        ()=>this.pathViewStore.changeTestPointById(mapPoint.pathTestPoint.id)
      )
    }
    if ( this.correlationVisible ) {
      this.onCorrelationRecordsLoaded([]);
      this.correlationTable.columnByName['pointid'].filter.value = mapPoint.pointId || mapPoint.exPointId;
      await this.correlationTable.load();
    }
  }


  changeSelectedSeqGuard = ( acceptFn: () => void ) => {
    console.log(`changeSelectedSeqGuard() seqEditor.isEditing=${this.seqEditor?.isEditing}`)
    if ( !this.isEditing() ) {
      acceptFn()
    } else {
      const resetSeqEditorForm = () => {
        this.seqEditor.isEditing = false
        this.seqEditor.reset()
        acceptFn()
      }
      this.seqEditor.askDiscardEdits(resetSeqEditorForm)
    }
  }


  async onMoveToPreviousSeq() {
    this.changeSelectedSeqGuard( () => {
      this.pathViewStore.moveToPreviousSeq()
    })
  }

  async onMoveToNextSeq() {
    this.changeSelectedSeqGuard( () => {
      this.pathViewStore.moveToNextSeq()
    })
  }

  // Save new/temp seq if a comment is saved before seq used
  async onCommentSaved( saveEvent: PathTestPointSeqSavedEvent ) {
    const { pathTestId, testPointId, seqId } = saveEvent
    const state = await firstValueFrom(this.pathViewStore.state$);
    if ( pathTestId != state.pathTestDoc._id ) {
      console.warn(`Skipping saved comment event for test seq since no longer the active pathTest (active=${state.pathTestDoc?._id}, saved=${pathTestId})`)
      return
    }
    const rxTestPoint = state.pathTestDoc.testPoints?.find(tp=>tp.id==testPointId)
    if ( !rxTestPoint ) {
      const errMsg = `Saved comment event for test seq but the testPoint is not found (testPointId=${testPointId})`
      console.error(errMsg)
      this.testSeqService.publishError(errMsg,this)
      return
    }
    const rxSeq = rxTestPoint.seqs?.find(seq=>seq.id==seqId)
    if ( !rxSeq ) {
      // state.seq is current value in UI and should match (state.seqs may be empty if it is "Not Started" point with no seq)
      const seq = state.seq?.id == seqId ? state.seq : (state.seqs.find(s=>s.id==seqId) || new PathTestPointSeq(seqId))
      console.log(`onCommentSaved(${JSON.stringify(saveEvent)}) - saving new seq associated with comment`)
      await this.pathViewStore.saveTestPointSeq(testPointId,seq,null)
    }
  }

  //
  // Functionality to walk test points and view correlation data in map
  //
  correlationVisible = false;
  hideTestPointsTable = false;
  correlationContext: string;
  bShowHandsets = true;
  bShowComments = true;
  @ViewChild("ptCorrelationTable") correlationTable: CorrelationTableComponent;
  @ViewChild("pathPtPlotter") pathPtPlotter: PathTestPlotterComponent;

  toggleCorrelationVisible = async () => {
    this.correlationVisible = !this.correlationVisible;
    if (!this.correlationVisible) {
      this.hideTestPointsTable = false;
    }

    if (!this.correlationVisible) {
      const map = this.pathPtPlotter;
      this.bShowComments = true;
      this.bShowHandsets = true;
      if (!map && !map.points) {
        console.warn("No map to plot correlation records");
        return;
      }
      map.points.forEach((mpt: PathTestMapPoint) => (mpt.miscChildren = []));
      map.plotWhenReady(map.points);
    } else {
      this.bShowComments = false;
      this.bShowHandsets = false;
      // template will add table and trigger load
      if (!this.correlationContext) {
        this.correlationContext = JSON.stringify(
          await this.buildCorrelationContext()
        );
      }
      if (!this.bShowMapInEditor) {
        this.showMapInEditor();
      }
    }
  };

  correlationRowHover = (
    hoverEvt: { event: MouseEvent; record: CorrelationRecord },
    active: boolean
  ) => {
    //console.log('correlationRowHover()', hoverEvt,active);
    this.pathPtPlotter?.highlightLeafNode(hoverEvt.record.viewrowid, active);
  };

  async showKmlViewer() {
    try {
      const mapPoints = [...this.pathPtPlotter?.points];
      const scoutPtInfoByPtId = { ...this.pathPtPlotter.scoutPtInfoByPtId };
      const pathTest = this.pathTestDoc;

      // correlationTable is not very performant so hide rows when showing cesium map
      let correlationTableVisible = true;
      if (this.correlationTable) {
        correlationTableVisible = this.correlationTable.isActiveView;
        this.correlationTable.isActiveView = false;
      }
      this.pathPtPlotter?.plotWhenReady([]);
      const buildingId = pathTest.buildingId;
      const paths = await this.pathWaypointService.findBuilding3dPaths(
        buildingId
      );
      const path = paths.find((p) => p.pathId == this.pathTestDoc.pathId);
      if (!path) {
        console.warn(
          "showKmlViewer() no path found for pathId",
          pathTest.pathId
        );
        this.appState.notifyError(
          `No path found for pathId "${pathTest.pathId}"`,
          this,
          10
        );
        return;
      }
      for (const mwpRow of path.mapWaypointRows) {
        const mapPoint = mapPoints.find((p) => p.pointId == mwpRow.pointId);
        // console.log({mapPoint});
        if (mapPoint) {
          const disabled =
            scoutPtInfoByPtId?.[mapPoint.pointId || mapPoint.exPointId]
              ?.scoutPoint?.disabled ?? false;
          mwpRow.mapWaypoint = mapPoint;
          mwpRow.mapWaypoint.disabled = disabled;
        }
      }
      const overlaySource =
        this.pathTestOverlay || (this.pathTestDoc as any)?.overlay;
      const overlaysByPathId = overlaySource
        ? { [pathTest.pathId]: overlaySource }
        : {};
      console.log("showKmlViewer()", { buildingId, paths });
      const modalRef = PathKmlViewerModal.show(this.bsModalService, {
        selectedPathIds: [pathTest.pathId],
        buildingId,
        paths,
        overlaysByPathId,
      });
      await firstValueFrom(modalRef.onHidden);
      if (this.correlationTable) {
        this.correlationTable.isActiveView = correlationTableVisible;
      }
      this.pathPtPlotter?.plotWhenReady(mapPoints);
    } catch (e: any) {
      console.error("showKmlViewer() error", e);
      this.appState.notifyError(
        "Issue preparing or rendering KML. " + e.message,
        this,
        10
      );
    }
  }

  onCorrelationRecordsLoaded = (rows: CorrelationRecord[]) => {
    const map = this.pathPtPlotter;
    if (!map) {
      console.warn("No map to plot correlation records");
      return;
    }
    const mapPoints = map?.points;
    mapPoints?.forEach((mpt: PathTestMapPoint) => {
      const ptCorrelationRecs = rows.filter((cr) => cr.pointid == mpt.pointId);
      mpt.miscChildren = [];
      const children: MiscLeafNode[] = mpt.miscChildren;
      ptCorrelationRecs.forEach((cr) => {
        if (cr.latitude != null && cr.longitude != null) {
          const child = {
            latitude: Number(cr.latitude),
            longitude: Number(cr.longitude),
            altitudeEllipsoidMeters: Number(cr.altitude),
            id: cr.viewrowid,
            parentId: mpt.id,
            title: `${cr.pointid} | ${cr.horizontalerror?.toFixed(2)}m | ${
              cr.carrier
            } | ${cr.type} | ${cr.summarytech}`,
            type: "call log",
            iconClass: isAndroid(cr.os)
              ? "fab fa-android android-icon"
              : isIos(cr.os)
              ? "fab fa-apple apple-icon"
              : undefined,
            data: cr,
          };
          children.push(child);
        }
      });
    });
    map.plotWhenReady(mapPoints);
  };

  async buildCorrelationContext(): Promise<Partial<TableViewContext>> {
    const pathTest = this.pathTestDoc;
    const vm = await firstValueFrom(this.pathViewStore.vm$);
    const testPoint = vm.testPoint;
    if (!testPoint) {
      console.warn("buildCorrelationContext() no testPoint");
      return {};
    }
    const ctx: Partial<TableViewContext> = {
      filters: [
        {
          key: "pointid",
          value: testPoint.pointId,
          mode: "=",
        },
        {
          key: "pathid",
          value: pathTest.pathId,
          mode: "=",
        },
        {
          key: "stage",
          value: pathTest.stage,
          mode: "=",
        },
        {
          key: "cycle",
          value: `${pathTest.cycle}`,
          mode: "=",
        },
        {
          key: "correlatedcall",
          value: "true",
          mode: "=",
        },
      ],
      visibleNames: [
        "os",
        "carrier",
        "modelname",
        "correlatedcall",
        "callstarttime",
        "callduration",
        "rantech",
        "summarytech",
        "type",
        "horizontalerror",
        "verticalerror",
        "locationtime",
        "timetofix",
      ],
      sorts: [
        {
          key: "horizontalerror",
          order: "DESC",
        },
        {
          key: "locationtime",
          order: "DESC",
        },
      ],
    };
    return ctx;
  }


  onMiscLeafNodeEvent = (evt: {srcEvent: MouseEvent, id : number, parentId: string}) => {
    if ( !this.correlationTable ) {
      return;
    }
    const recIndex = this.correlationTable.records.findIndex(cr=>cr.viewrowid == evt.id);
    if ( recIndex === -1 ) {
      console.warn('onMiscLeafNodeEvent() no correlation record found for id', evt.id);
      return;
    }
    const rec = this.correlationTable.records[recIndex]
    if ( evt.srcEvent.type==='click' ) {
      this.correlationTable.showDetailModal(rec);
    } else if ( evt.srcEvent.type==='mouseover') {
      this.correlationTable.applySelectedIndices({
        selectedIndices:[recIndex],
        clearPreviousSelection:true,
        scrollIntoViewMode: false //TODO - only support scroll into view from a popup menu item
      });
    } else if (evt.srcEvent.type==='mouseout') {
      this.correlationTable.applySelectedIndices({
        selectedIndices:[],
        clearPreviousSelection:true,
      });
    }
  }
}
